import { syntheticVisit } from './synthetic-visit';

export type AnalyticsEvent =
  | 'first_visit_viewed'
  | 'category_selected'
  | 'venue_selected'
  | 'candidate_added'
  | 'compare_viewed'
  | 'decision_saved'
  | 'evidence_unknown_shown'
  | 'unknown_dwell_30s'
  | 'session_exit'
  | 'layout_version_selected'
  | 'review_published'
  | 'actual_price_entered';

type LayoutVersionSelection = Readonly<{
  stageConfig: 'known_layout' | 'unconfirmed_layout';
  source: 'interactive_map' | 'manual_coordinates';
}>;

/** H-2에 필요한 최소 공개 리뷰 완료 신호. 좌석·공연·가격·계정·proof 식별자는 절대 포함하지 않는다. */
export type ReviewPublishedSignal = Readonly<{
  verification: 'purchase' | 'unverified';
  tabsFilled: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** v1에는 공개 리뷰 사진 업로드가 없으므로 항상 false. 인증 가림본은 공개 사진이 아니다. */
  hasPhoto: boolean;
}>;

/** H-3 가격 기여 신호. 금액은 가격 분포·리뷰 DB에만 남고 Analytics Engine에는 보내지 않는다. */
export type ActualPriceEnteredSignal = Readonly<{
  transferType: 'none';
  includesPoints: boolean;
}>;

/** H-4는 좌석·공연·계정 식별자 없이, 공식 배치도 여부만 분리한다. */
export type UnknownEvidenceSignal = Readonly<{
  layoutVersionKnown: boolean;
}>;

/** H-4 보조 이탈 신호. pagehide 유실을 주 KPI로 쓰지 않는다. */
export type SessionExitSignal = Readonly<{
  lastSurface: 'evidence_unknown';
  dwellBucket: 'under_30s' | 'at_least_30s';
}>;

export function reviewPublishedSignal(
  verification: ReviewPublishedSignal['verification'],
  scores: Readonly<Record<string, number | null>>,
): ReviewPublishedSignal {
  const tabsFilled = Object.values(scores).filter((score) => score !== null).length;
  return {
    verification,
    tabsFilled: Math.min(6, tabsFilled) as ReviewPublishedSignal['tabsFilled'],
    hasPhoto: false,
  };
}

const allowedEvents = new Set<AnalyticsEvent>([
  'first_visit_viewed',
  'category_selected',
  'venue_selected',
  'candidate_added',
  'compare_viewed',
  'decision_saved',
  'evidence_unknown_shown',
  'unknown_dwell_30s',
  'session_exit',
  'layout_version_selected',
  'review_published',
  'actual_price_entered',
]);

export function track(event: 'layout_version_selected', selection: LayoutVersionSelection): void;
export function track(event: 'review_published', signal: ReviewPublishedSignal): void;
export function track(event: 'actual_price_entered', signal: ActualPriceEnteredSignal): void;
export function track(event: 'evidence_unknown_shown' | 'unknown_dwell_30s', signal: UnknownEvidenceSignal): void;
export function track(event: 'session_exit', signal: SessionExitSignal): void;
export function track(event: Exclude<AnalyticsEvent, 'layout_version_selected' | 'review_published' | 'actual_price_entered' | 'evidence_unknown_shown' | 'unknown_dwell_30s' | 'session_exit'>): void;
export function track(event: AnalyticsEvent, selection?: LayoutVersionSelection | ReviewPublishedSignal | ActualPriceEnteredSignal | UnknownEvidenceSignal | SessionExitSignal): void {
  if (!allowedEvents.has(event)) return;
  const requiresSignal = event === 'layout_version_selected'
    || event === 'review_published'
    || event === 'actual_price_entered'
    || event === 'evidence_unknown_shown'
    || event === 'unknown_dwell_30s'
    || event === 'session_exit';
  const payload = requiresSignal
    ? selection ? { event, occurredAt: new Date().toISOString(), ...selection } : null
    : { event, occurredAt: new Date().toISOString() };
  if (!payload) return;
  void fetch('/api/events', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...payload, synthetic: syntheticVisit() }),
    keepalive: true,
  }).catch(() => undefined);
}
